import graphqlClient from "@/GQLClient";
import type { AuditLogFragment, PlannerTaskFragment } from "@/graphql";
import {
  DeletePlannerTemplateDocument,
  GetPlannerCategoriesDocument,
  GetPlannerTemplatesDocument,
  SavePlannerCategoryDocument,
  SavePlannerTemplateDocument,
  type PlannerCategoryInput,
  GetPlannerFiltersDocument,
  type PlannerFilterInput,
  SavePlannerFilterDocument,
  DeletePlannerFilterDocument,
  type PlannerTemplateInput,
  AuditLogQueryDocument,
  Audit_Event_Entity,
  GetPlannerTasksDocument,
  GetSinglePlannerTaskDocument,
  GetPlannerSubTasksDocument,
  CompletePlannerSubtaskDocument,
  UncompletePlannerSubtaskDocument,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import apiclient from "@/services/apiclient";
import { useAuthStore } from "@/stores/auth.module";
import type { NewFilterTypes } from "@/types";

const getFilters = async () => {
  const response = await apiclient.get("planner/get-filters");
  return response.data;
};

const getFiltersViaGQL = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerFiltersDocument,
  });

  if (response.data.planner?.getFilters) {
    return response.data.planner?.getFilters as NewFilterTypes[];
  }

  throw new SentryGraphQLError();
};

const getCategories = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerCategoriesDocument,
  });

  if (response.data.planner?.getCategories) {
    return response.data.planner?.getCategories;
  }

  throw new SentryGraphQLError();
};

const saveCategory = async (category: PlannerCategoryInput) => {
  const authStore = useAuthStore();
  category.institution = authStore.institutionId;

  const response = await graphqlClient.mutate({
    mutation: SavePlannerCategoryDocument,
    variables: {
      category,
    },
  });

  if (response.data?.planner?.saveCategory?.success) {
    return response.data.planner.saveCategory;
  }

  throw new SentryGraphQLError();
};

const saveFilter = async (filter: PlannerFilterInput) => {
  const authStore = useAuthStore();
  filter.institution = authStore.institutionId;

  const response = await graphqlClient.mutate({
    mutation: SavePlannerFilterDocument,
    variables: {
      filter,
    },
  });

  if (response.data?.planner?.saveFilter?.success) {
    return response.data.planner.saveFilter;
  }

  throw new SentryGraphQLError();
};

const deleteFilter = async (id: string) => {
  const response = await graphqlClient.mutate({
    mutation: DeletePlannerFilterDocument,
    variables: {
      id,
    },
  });

  if (response.data?.planner?.deleteFilter?.success) {
    return response.data.planner.deleteFilter;
  }

  throw new SentryGraphQLError();
};

const getPlannerTemplates = async () => {
  const response = await graphqlClient.query({
    query: GetPlannerTemplatesDocument,
  });

  if (response.data.plannerTemplates) {
    return response.data.plannerTemplates;
  }

  throw new SentryGraphQLError();
};

const deletePlannerTemplates = async (id: string) => {
  const response = await graphqlClient.mutate({
    mutation: DeletePlannerTemplateDocument,
    variables: {
      id,
    },
  });

  if (response.data?.planner?.deleteTemplate?.success) {
    return response.data.planner.deleteTemplate;
  }

  throw new SentryGraphQLError();
};

const saveTemplate = async (template: PlannerTemplateInput) => {
  const response = await graphqlClient.mutate({
    mutation: SavePlannerTemplateDocument,
    variables: {
      template,
    },
  });

  if (response.data?.planner?.saveTemplate?.success) {
    return response.data.planner.saveTemplate;
  }

  throw new SentryGraphQLError();
};

const getAuditLog = async (
  entityId: string[],
  entityType: Audit_Event_Entity,
): Promise<AuditLogFragment[]> => {
  const response = await graphqlClient.query({
    query: AuditLogQueryDocument,
    variables: {
      filters: { entityId, entityType },
    },
  });

  if (response.data.auditLog?.getByFilters) {
    return response.data.auditLog?.getByFilters;
  }

  throw new SentryGraphQLError();
};

const completePlannerSubtask = async (
  plannerTaskId: string,
  plannerSubtaskId: string,
) => {
  const response = await graphqlClient.mutate({
    mutation: CompletePlannerSubtaskDocument,
    variables: {
      plannerTaskId,
      plannerSubtaskId,
    },
  });

  if (response.data?.completePlannerSubtask?.success) {
    return response.data.completePlannerSubtask;
  }

  throw new SentryGraphQLError();
};

const uncompletePlannerSubtask = async (
  plannerTaskId: string,
  plannerSubtaskId: string,
) => {
  const response = await graphqlClient.mutate({
    mutation: UncompletePlannerSubtaskDocument,
    variables: {
      plannerTaskId,
      plannerSubtaskId,
    },
  });

  if (response.data?.uncompletePlannerSubtask?.success) {
    return response.data.uncompletePlannerSubtask;
  }

  throw new SentryGraphQLError();
};

const getPlannerTasks = async (dateFrom: string, dateTo: string) => {
  const response = await graphqlClient.query({
    query: GetPlannerTasksDocument,
    variables: {
      dateFrom,
      dateTo,
    },
  });

  if (response.data.planner?.getTasksByFilters) {
    return response.data.planner?.getTasksByFilters;
  }

  throw new SentryGraphQLError();
};

const getSinglePlannerTask = async (id: string) => {
  const response = await graphqlClient.query({
    query: GetSinglePlannerTaskDocument,
    variables: {
      id,
    },
  });

  if (response.data.plannerTaskEntry) {
    return response.data.plannerTaskEntry as PlannerTaskFragment;
  }

  throw new SentryGraphQLError();
};

const getSubtasks = async (id: string) => {
  const response = await graphqlClient.query({
    query: GetPlannerSubTasksDocument,
    variables: {
      id,
    },
  });

  if (response.data.plannerTaskEntry) {
    return response.data.plannerTaskEntry;
  }

  throw new SentryGraphQLError();
};

export default {
  // Categories
  getCategories,
  saveCategory,

  // Filters
  getFilters,
  getFiltersViaGQL,
  saveFilter,
  deleteFilter,

  // Templates
  getPlannerTemplates,
  deletePlannerTemplates,
  saveTemplate,

  // Audit
  getAuditLog,

  // Tasks
  completePlannerSubtask,
  uncompletePlannerSubtask,
  getPlannerTasks,
  getSinglePlannerTask,
  getSubtasks,
};
